import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  Gem, 
  Star, 
  ArrowRight, 
  Info,
  AlertCircle,
  CheckCircle,
  X,
  Lightbulb,
  Shield,
  Droplets,
  ZoomIn,
  Mountain,
  Sun
} from "lucide-react";
import SEOHead from "../components/SEOHead";

const AboutOpalPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  const openLightbox = (image, title) => {
    setLightboxImage({ src: image, title });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "About Australian Opals",
    "description": "Learn about Australian opals including solid opals, doublets, and triplets. Understand the different types, their characteristics, and how to care for your opal jewelry.",
    "url": "https://umoonaopalmine.com.au/about-opal"
  };

  const opalTypes = [
    {
      name: "Solid Opals",
      description: "100% natural opal, shaped and polished to highlight their brilliance",
      features: ["100% natural opal", "Most valuable and durable", "Full depth of color", "Can be worn daily", "Suitable for all jewelry types"],
      image: "/assets/SOLID-OPAL.png"
    },
    {
      name: "Doublet Opals", 
      description: "Thin slice of light opal bonded to a darker backing to enhance color intensity",
      features: ["Thin opal layer on dark backing", "Enhanced color intensity", "More affordable than solid", "Good for pendants and earrings", "Not suitable for rings"],
      image: "/assets/OPAL-DOUBLET.png"
    },
    {
      name: "Triplet Opals",
      description: "Three-layer construction with clear protective top of quartz or crystal glass",  
      features: ["Three-layer construction", "Clear protective cap", "Magnified colors", "Most affordable option", "Best for low-impact jewelry"],
      image: "/assets/OPAL-TRIPLET.png"
    }
  ];

  const careInstructions = {
    dos: [
      "Clean with mild soap and water",
      "Store in a soft cloth or padded box", 
      "Remove before swimming or bathing",
      "Keep away from harsh chemicals",
      "Have settings checked regularly"
    ],
    donts: [
      "Expose to extreme temperature changes",
      "Use ultrasonic cleaners", 
      "Expose doublets/triplets to water, detergents, or oils",
      "Wash hands while wearing composite opal jewelry",
      "Wear during heavy physical activities"
    ]
  };

  return (
    <div>
      <SEOHead
        title="About Australian Opals - Types, Care & Information | Umoona Opal Mine"
        description="Learn about Australian opals including solid opals, doublets, and triplets. Understand the different types, their characteristics, and how to care for your opal jewelry."
        keywords="Australian opals, opal types, solid opal, doublet opal, triplet opal, opal care, Coober Pedy opals, opal information"
        canonical="/about-opal"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-purple-800 to-indigo-900">
        <div className="absolute inset-0">
          <img 
            src="/assets/Turbo360-1043165.jpg"
            alt="Australian Opals Background"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <Badge variant="secondary" className="mb-6 bg-orange-500/20 text-orange-300 border border-orange-400/30 px-6 py-3 text-lg">
            💎 Australia's National Gemstone
          </Badge>
          
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            About Australian Opals
          </h1>
          
          <p className="text-xl lg:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
            Discover the beauty, types, and characteristics of one of the world's most unique gemstones
          </p>
        </div>
      </section>

      {/* What is Opal Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                What is Opal?
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Opals are Australia's national gemstone, renowned worldwide for their unique play of color. 
                  Formed over millions of years, these precious stones contain tiny silica spheres that diffract 
                  light to create their characteristic rainbow of colors.
                </p>
                <p>
                  Opals can be treated, cut, polished, and set in different ways to bring out their natural beauty. 
                  They are generally classified as solid opals or composite opals.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="/assets/2022-1018205.jpg"
                alt="Beautiful Australian Opal"
                className="w-full rounded-xl shadow-lg"
              />
              <p className="text-sm text-gray-500 mt-2 text-center italic">
                Beautiful Australian Opal
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Opal Formed in Coober Pedy - Geological History */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-6 bg-blue-500/20 text-blue-600 border border-blue-400/30 px-6 py-3 text-lg">
              🌏 Geological Marvel
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How Opal Formed in Coober Pedy
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              The story of Coober Pedy's opals begins over 100 million years ago, in a time when Australia's landscape looked vastly different
            </p>
          </div>

          {/* Timeline of Formation */}
          <div className="space-y-12 mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 md:p-12 border-2 border-blue-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Droplets className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Stage 1: The Ancient Sea (130-100 Million Years Ago)</h3>
                  <p className="text-blue-900 font-semibold text-lg">Cretaceous Period - When Dinosaurs Roamed</p>
                </div>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  During the Cretaceous period, much of Australia was covered by a vast <strong>inland sea called the Eromanga Sea</strong>. This shallow, warm sea stretched across what is now South Australia, covering the area that would become Coober Pedy.
                </p>
                <p>
                  The seabed was rich in silica-bearing sediments - the key ingredient for opal formation. Marine creatures lived and died in these waters, leaving behind shells, bones, and organic material that would eventually become fossilized. This is why we sometimes find <strong>opalised fossils</strong> in Coober Pedy, including ancient marine reptiles, shells, and even dinosaur bones turned to precious opal!
                </p>
                <div className="bg-white/60 p-4 rounded-xl mt-4">
                  <p className="font-semibold text-blue-900 mb-2">Key Fact:</p>
                  <p className="text-gray-700">
                    The Eromanga Sea was one of the largest inland seas in Earth's history, covering an area larger than modern-day Queensland. Its sediments would become the foundation for Australia's precious opal deposits.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 border-2 border-amber-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mountain className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Stage 2: The Sea Retreats (100-65 Million Years Ago)</h3>
                  <p className="text-amber-900 font-semibold text-lg">Uplift and Climate Change</p>
                </div>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  As tectonic forces caused the Australian continent to rise, the inland sea gradually retreated. The sediments that had accumulated on the seabed - layers of sandstone, claystone, and siltstone - were exposed to the atmosphere.
                </p>
                <p>
                  The climate became increasingly arid, and the region began to resemble the outback we know today. But beneath the surface, something remarkable was happening. The sedimentary rocks contained <strong>high concentrations of silica</strong> (silicon dioxide), dissolved from decomposing minerals in the ancient seabed.
                </p>
                <p>
                  Seasonal rainfall began to seep down through cracks, fissures, and decomposed fossils in the porous sedimentary rock. As this silica-rich water moved through the ground, it began a process that would take millions of years to complete.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 md:p-12 border-2 border-purple-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gem className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Stage 3: Opal Formation (30-5 Million Years Ago)</h3>
                  <p className="text-purple-900 font-semibold text-lg">The Miracle of Silica Spheres</p>
                </div>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  This is where the magic happened. Over millions of years, the silica-rich water slowly evaporated in underground cavities, leaving behind deposits of <strong>microscopic silica spheres</strong>. These perfectly uniform spheres - measuring between 150 to 400 nanometers in diameter - stacked together in regular patterns.
                </p>
                <p>
                  The size and arrangement of these spheres determines the <strong>color of the opal</strong>. When light passes through the structured array of silica spheres, it diffracts (bends and separates), creating the spectacular "play-of-color" or fire that opals are famous for. This optical phenomenon is called <strong>precious opal</strong>.
                </p>
                <div className="bg-white/60 p-4 rounded-xl mt-4">
                  <p className="font-semibold text-purple-900 mb-2">The Science of Color:</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Red fire</strong> - Larger silica spheres (around 350nm)</li>
                    <li>• <strong>Green fire</strong> - Medium spheres (around 250nm)</li>
                    <li>• <strong>Blue fire</strong> - Smaller spheres (around 200nm)</li>
                    <li>• The most valuable opals display multiple colors across their surface</li>
                  </ul>
                </div>
                <p>
                  Opal formed wherever the silica solution could seep: in cracks and fissures in the rock, in decomposed fossils, between rock layers, and in voids left by decomposed wood or shells. This is why Coober Pedy opal can be found as <strong>seam opal</strong> (in horizontal layers), <strong>nobby opal</strong> (rounded formations), or <strong>matrix opal</strong> (scattered through the host rock).
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-3xl p-8 md:p-12 border-2 border-green-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Sun className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Stage 4: The Present Day</h3>
                  <p className="text-green-900 font-semibold text-lg">Discovery and Mining</p>
                </div>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  The opal deposits remained hidden beneath the desert until 1915, when teenager Willie Hutchison stumbled upon them while searching for water. Today, Coober Pedy produces <strong>over 70% of the world's precious opal</strong>.
                </p>
                <p>
                  The opal-bearing level, known as the "opal dirt" or "level," is typically found at depths of <strong>6 to 30 meters (20 to 100 feet)</strong> below the surface, within the sedimentary rock layers. Miners dig down through layers of overburden (non-opal-bearing rock) to reach this precious level.
                </p>
                <p>
                  What makes Coober Pedy special is the sheer quantity and quality of opal found here. The combination of the ancient Eromanga Sea's mineral-rich sediments, millions of years of favorable geological conditions, and the region's unique climate created the perfect recipe for opal formation on an extraordinary scale.
                </p>
              </div>
            </div>
          </div>

          {/* Why Coober Pedy is Unique */}
          <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-3xl p-8 md:p-12 border-2 border-orange-300">
            <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Coober Pedy's Opals Are Unique</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/80 p-6 rounded-xl">
                <h4 className="font-bold text-xl text-gray-900 mb-3 flex items-center gap-2">
                  <Gem className="w-6 h-6 text-orange-600" />
                  Crystal Quality
                </h4>
                <p className="text-gray-700">
                  The long, stable formation period allowed for exceptional crystal structure. Coober Pedy opals often display more intense and varied color play than opals from other regions.
                </p>
              </div>

              <div className="bg-white/80 p-6 rounded-xl">
                <h4 className="font-bold text-xl text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="w-6 h-6 text-orange-600" />
                  Variety of Types
                </h4>
                <p className="text-gray-700">
                  From brilliant light crystal opal to the rare and valuable black opal, Coober Pedy produces every type of precious opal, each formed in slightly different conditions.
                </p>
              </div>

              <div className="bg-white/80 p-6 rounded-xl">
                <h4 className="font-bold text-xl text-gray-900 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-6 h-6 text-orange-600" />
                  Opalised Fossils
                </h4>
                <p className="text-gray-700">
                  The ancient marine environment means fossils here were perfectly preserved before being replaced by opal, creating stunning specimens that are both scientifically and aesthetically valuable.
                </p>
              </div>

              <div className="bg-white/80 p-6 rounded-xl">
                <h4 className="font-bold text-xl text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-orange-600" />
                  Stable Deposits
                </h4>
                <p className="text-gray-700">
                  The opal-bearing layer is extensive and consistent, making Coober Pedy the world's most reliable source of quality precious opal for over a century.
                </p>
              </div>
            </div>
          </div>

          {/* Fun Facts */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold mb-6 text-center">Fascinating Opal Facts</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-xl">💧</span>
                </div>
                <p>Opal can contain up to 20% water trapped within its silica structure - one reason why proper care is essential</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-xl">⏰</span>
                </div>
                <p>It took between 5 to 30 million years for the opal in your jewelry to form - truly a gemstone of geological time</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-xl">🔬</span>
                </div>
                <p>Each opal's pattern is absolutely unique - like a fingerprint, no two opals are exactly alike</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-xl">🦕</span>
                </div>
                <p>Coober Pedy has produced opalised dinosaur bones, including parts of plesiosaurs - marine reptiles that lived in the ancient sea</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Opal */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Types of Opal
            </h2>
          </div>

          <div className="space-y-12">
            {opalTypes.map((type, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    <div className="p-8 space-y-6">
                      <h3 className="text-2xl font-bold text-gray-900">{type.name}</h3>
                      <p className="text-gray-600 text-lg">{type.description}</p>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                        <ul className="space-y-2">
                          {type.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                              <span className="text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div 
                      className="bg-gray-100 flex items-center justify-center p-8 relative group cursor-pointer"
                      onClick={() => openLightbox(type.image, `${type.name} Structure`)}
                    >
                      <img 
                        src={type.image}
                        alt={`${type.name} Structure`}
                        className="max-w-full max-h-64 object-contain hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                        <div className="bg-white/90 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                          <ZoomIn className="w-4 h-4 mr-1" />
                          Click to enlarge
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Care Instructions */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Caring for Your Opal Jewelry
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 border-green-200 bg-green-50">
                <CardContent className="space-y-4 p-0">
                  <h3 className="text-xl font-bold text-green-800 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    ✓ Do:
                  </h3>
                  <ul className="space-y-2">
                    {careInstructions.dos.map((instruction, idx) => (
                      <li key={idx} className="text-green-700">• {instruction}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="p-6 border-red-200 bg-red-50">
                <CardContent className="space-y-4 p-0">
                  <h3 className="text-xl font-bold text-red-800 flex items-center">
                    <X className="w-5 h-5 mr-2" />
                    ✗ Don't:
                  </h3>
                  <ul className="space-y-2">
                    {careInstructions.donts.map((instruction, idx) => (
                      <li key={idx} className="text-red-700">• {instruction}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <img 
                src="/assets/2022-1018160.jpg"
                alt="Well-maintained opal jewelry examples"
                className="w-full rounded-xl shadow-lg"
              />
              <div className="text-center">
                <h4 className="font-bold text-gray-900 mb-2">Pristine Opal Jewelry</h4>
                <p className="text-gray-600">Properly cared for opal jewelry maintains its beauty for generations</p>
              </div>
            </div>
          </div>

          <Alert className="mt-12 border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Important:</strong> Doublet and triplet opals being layered should not be brought into contact with water, 
              detergent or oils as the glues could be compromised. Always remove composite opal jewelry before washing hands or using cleaning products.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to Own Authentic Australian Opals?
              </h2>
              <p className="text-xl text-purple-100 max-w-2xl mx-auto">
                Browse our collection of handpicked opals or visit our underground museum to learn more
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-purple-600 hover:bg-purple-50 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/online-store">
                  Shop Authentic Opals
                  <Gem className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-purple-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/free-museum">
                  Visit Free Museum
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50 p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-gray-800 hover:bg-gray-900 text-white p-2 rounded-full transition-all duration-300 shadow-lg"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title}
              className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutOpalPage;